import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import NeoLoader from "../../../components/Common/NeoLoader";
import { Breadcrumb, NeoAlert, NeoDateRangePicker, NeoButton } from "../../../components/Common";
import { useReceivables } from "./hooks/useReceivables";
import ReceivableTable from "./components/ReceivableTable";

/**
 * ReceivablesPage
 * Tela de Contas a Receber — lista financialRecords do tipo ASSET.
 * Por padrão carrega apenas o DIA ATUAL (econômico).
 */
const ReceivablesPage = () => {
    document.title = "Recebíveis | NeoApp - Sistema de Gestão";

    const printRef = useRef();

    const {
        records,
        summary,
        loading,
        error,
        hasMore,
        processing,
        statusFilter,
        search,
        startDate,
        endDate,
        setStatusFilter,
        setSearch,
        setStartDate,
        setEndDate,
        handleSearch,
        hasSearched,
        actions,
    } = useReceivables();

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: `Contas_a_Receber_${startDate}_${endDate}`,
    });

    return (
        <React.Fragment>
            {/* TOP BAR: Cabeçalho & Filtro de Período Lado a Lado (Super Limpo e Sem Card) */}
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-4 gap-3">
                <div>
                    <Breadcrumb title="Contas a Receber" />
                    <p className="text-muted mb-0 font-size-13 mt-0.5">
                        Controle de faturamento, recebíveis, baixas e conciliação de vendas do sistema.
                    </p>
                </div>

                <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 flex-shrink-0">
                    <NeoDateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onChange={({ startDate: start, endDate: end }) => {
                            if (start !== undefined) setStartDate(start);
                            if (end !== undefined) setEndDate(end);
                        }}
                        size="sm"
                        className="mb-0 neo-daterangepicker-filter"
                        placeholder="Selecione o período..."
                    />
                    <NeoButton
                        variant="primary"
                        onClick={handleSearch}
                        loading={loading}
                        title="Buscar"
                        icon="mdi-magnify"
                        size="sm"
                        square
                        className="neo-filter-search-btn"
                    />
                    <NeoButton
                        variant="outline-secondary"
                        onClick={handlePrint}
                        title="Imprimir Contas a Receber"
                        icon="mdi-printer"
                        size="sm"
                        square
                        className="neo-filter-search-btn"
                    />
                </div>
            </div>
            
            <div className="border-top mb-4"></div>

            {/* ═══ LOADING INICIAL ═══ */}
            {loading && !records.length && (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                    <NeoLoader type="inline" loading={loading} />
                </div>
            )}

            {/* ═══ ERRO ═══ */}
            {error && !loading && (
                <NeoAlert color="danger">
                    <div className="d-flex align-items-center">
                        <i className="mdi mdi-alert-circle-outline me-2 fs-5" />
                        <div>
                            <strong>Erro ao carregar dados:</strong> {error?.message || String(error)}
                            <button className="btn btn-link btn-sm p-0 ms-2 fw-bold text-danger" onClick={actions.refresh}>
                                Tentar novamente
                            </button>
                        </div>
                    </div>
                </NeoAlert>
            )}

            {/* ═══ CONTEÚDO ═══ */}
            {(!loading || records.length > 0) && !error && (
                <div ref={printRef}>
                    <ReceivableTable
                        records={records}
                        summary={summary}
                        processing={processing}
                        search={search}
                        statusFilter={statusFilter}
                        onSearchChange={setSearch}
                        onStatusChange={setStatusFilter}
                        onMarkAsPaid={actions.markAsPaid}
                        onCancel={actions.cancelRecord}
                        onLoadMore={actions.loadMore}
                        hasMore={hasMore}
                        loading={loading}
                        hasSearched={hasSearched}
                    />
                </div>
            )}
        </React.Fragment>
    );
};

export default ReceivablesPage;
